/**
 * Pass-and-play reaction prompt.
 *
 * Surfaces on the active viewer's screen when their OPPONENT has at least one
 * legal reaction to the most recent action. Two buttons:
 *   - Yes \u2014 take control (handoff to opponent's screen, opening the reaction
 *     window). The parent sets `pendingPassTo` to the opponent.
 *   - No  \u2014 dismiss for the current actionChain entry. The badge does not
 *     reappear for that same entry; engine continues with base flow.
 *
 * Important: the active viewer's screen does NOT see WHICH cards the opponent
 * holds. It only sees that some legal reaction exists. This is the (a) flow
 * Noah picked: opponent stays anonymous until they decide to react.
 */
import { Button } from "@/components/ui/Button";
import { Zap } from "lucide-react";

interface ReactionPromptBadgeProps {
  opponentName: string;
  onAccept: () => void;
  onDecline: () => void;
}

export function ReactionPromptBadge({
  opponentName,
  onAccept,
  onDecline,
}: ReactionPromptBadgeProps) {
  return (
    <div
      className="fixed inset-x-0 top-1/2 z-[70] flex -translate-y-1/2 justify-center px-4"
      role="alertdialog"
      aria-label={`${opponentName} may react`}
    >
      <div className="pointer-events-auto flex w-full max-w-md flex-col items-center gap-3 rounded-lg border border-amber-soft/50 bg-[#13151a] px-5 py-4 shadow-[0_0_40px_rgba(245,158,11,0.2)]">
        <div className="flex items-center gap-2 text-amber-soft">
          <Zap size={16} />
          <span className="text-sm font-semibold">
            {opponentName} may want to respond
          </span>
        </div>
        <p className="text-center text-xs text-[#94a3b8]">
          Pass the device so they can take their reaction window?
        </p>
        <div className="flex gap-2">
          <Button onClick={onAccept} className="gap-1">
            Yes — pass to {opponentName}
          </Button>
          <Button variant="secondary" onClick={onDecline}>
            No, continue
          </Button>
        </div>
      </div>
    </div>
  );
}
